/**
 * The page catalogue: the sidebar nav AND the Admin → User-Management role
 * editor's per-page permission checkboxes (#599), the alias map RBAC
 * canonicalises through (#719) and the write-action registry behind the Data
 * Entry rule (#779).
 *
 * FEATREG2 (#3711): everything here is GENERATED from the feature registry
 * (feature-registry.ts: FEATURE_SECTIONS + FEATURE_REGISTRY). Add, move or
 * rename a page there; nothing in this file is hand-kept except
 * NON_CATALOG_ROUTES and API_PATH_EQUIVALENCES, which are not features.
 * tests/page-catalogue-role-matrix-3711.test.ts holds every role's sidebar
 * rows and gate verdicts against a reviewed fixture.
 *
 * The Sidebar component (`src/client/components/layout/Sidebar.tsx`) renders nav
 * sections, order, labels and dividers from `SIDEBAR_SECTIONS` through the shared
 * nav rule (sidebar-visibility.ts); it owns only the per-path icon map. The role
 * editor renders a checkbox for every page in `ALL_PAGE_PATHS`.
 *
 * Field semantics (set from the registry's `nav` block):
 *  - `navHidden`   — page is part of the catalog (so the role editor governs it) but the
 *                    Sidebar SKIPS it (routed/governable but not in the visible nav,
 *                    e.g. /display, /calendar).
 *  - `divider`     — a sub-divider LABEL rendered between pages in a section (e.g.
 *                    "More tools"). Has no path; carries only a `label`. Not a page,
 *                    so it is NOT included in `ALL_PAGE_PATHS`. Emitted from a
 *                    registry entry's `nav.dividerBefore`.
 *  - section `adminOnly` — the whole section only renders in the nav when the user is an
 *                    admin (e.g. "Work In Progress"). Pages still appear in the role editor.
 *
 * ORDER IS LOAD-BEARING: the order of sections, and of pages/dividers within each
 * section, is exactly the visible nav order; it is the registry's order.
 * `tests/sidebar-catalog-parity.test.ts` locks the structural invariants.
 */

// Extension kept so plain-node scripts (rbac-v2 seeds, reachability, mobile route plan) can import this module.
import { FEATURE_REGISTRY, FEATURE_SECTIONS, type FeatureEntry, type FeatureSection } from "./feature-registry.ts";
import type { WriteAction } from "./rbac";

// Routes deliberately outside the sidebar/role-editor catalog. This classifies
// coverage exceptions only; it does not grant access. New exceptions need a reason.
export const NON_CATALOG_ROUTES: ReadonlySet<string> = new Set([
  "*", // SPA fallback redirects to /
  "/login", // pre-auth
  "/auth/return", // OAuth post-callback destination restorer
  "/welcome", // public landing
  "/auth-error", // sign-in failure, no session required
  "/install", // #2005 public home-screen steps, static, no session required
  "/pending", // post-signup holding page, no role yet
  "/commercial", // redirect to /commercial/deliveries, outside AppShell (#3503)
  "/portal/:token", // token-gated customer portal
  "/shared/:token", // token-gated shared conversation
  "/projects/:id", // detail reached from /projects
  "/rd/mix-designs/compare", // redirect helper reached from Mix Designs
  "/artifacts/:id", // detail reached from /artifacts
  "/commercial/cases/:id", // detail reached from /commercial/cases
  "/commercial/cases/accounts/:id", // account detail reached from the cases board
  "/admin/ai-feedback/conversations/:id", // #3286 detail reached from AI Feedback; inherits its page grant
  "/rm-materials/:code", // RMVIEW #3386 one material's records, reached from /rm-materials; inherits its page grant
  "/station", // GUSSTATION #4447 kiosk keypad, outside AppShell; gated by the registered-station cookie, no page grant
]);

export interface SidebarPage {
  path: string;
  label: string;
  /** If true, the Sidebar nav skips this page; it still appears in the role editor + ALL_PAGE_PATHS. */
  navHidden?: boolean;
  /** If true, this entry is a sub-divider LABEL (no path), not a page. */
  divider?: boolean;
  /**
   * #779: true when the page has a REAL app write path (a gated write action —
   * see PAGE_WRITE_ACTIONS). Governs "Data Entry" divider membership: a page may
   * only sit under a Data Entry divider when this is true. Set from the
   * registry's `writes`; tests/data-entry-divider-writes.test.ts locks the two.
   */
  writes?: boolean;
  /**
   * #719: RETIRED paths that now resolve to this page (renames + Page-merges).
   * Each alias gets (a) a client redirect (old URL/bookmark -> this path, see
   * App.tsx) and (b) alias-aware RBAC (a grant on either the old or new path
   * counts for both — see canonicalPath/isPathAllowed in rbac.ts). An alias must
   * NOT also be a live catalog path — tests/path-aliases.test.ts guards it.
   */
  aliases?: string[];
  /**
   * #714: optional tab targeting for aliases — old path -> the merged page's tab key,
   * so a retired path can land on ITS tab (e.g. /waste -> /materials?tab=waste).
   * Consumed by ALIAS_REDIRECTS (client redirects only) — RBAC keeps using the
   * pure-path PATH_ALIASES.
   */
  aliasTabs?: Record<string, string>;
  /**
   * Live routes that belong to this same Page/RBAC unit. Unlike `aliases`, these
   * routes are not retired or redirected. A grant on any route in the group
   * grants the whole Page.
   */
  accessAliases?: readonly string[];
  /**
   * #2064: nav NESTING only. The page renders one step indented beneath the
   * catalog page at `parent` (same section, listed before it, not navHidden —
   * tests/sidebar-catalog-parity.test.ts guards all three). Own route, own RBAC
   * unit, own role-editor checkbox.
   */
  parent?: string;
  /**
   * #2545: this PAGE only renders in the nav for admins. RBAC is unchanged: it
   * hides a nav row, it does not secure anything. A page hidden this way MUST
   * also be hard-gated at its API (requireAdminMiddleware in server/index.ts).
   */
  adminOnly?: boolean;
}

export interface SidebarSection {
  key: string;
  label: string;
  /** If true, the whole section only renders in the nav for admins (pages still governable in the role editor). */
  adminOnly?: boolean;
  pages: SidebarPage[];
}

/** One registry entry as the catalogue page the Sidebar and role editor consume. */
function toSidebarPage(entry: FeatureEntry): SidebarPage {
  const { nav } = entry;
  return {
    path: entry.route,
    label: entry.name,
    ...(nav.navHidden ? { navHidden: true } : {}),
    ...(entry.writes.length > 0 ? { writes: true } : {}),
    ...(nav.aliases?.length ? { aliases: [...nav.aliases] } : {}),
    ...(nav.aliasTabs ? { aliasTabs: { ...nav.aliasTabs } } : {}),
    ...(nav.accessAliases?.length ? { accessAliases: [...nav.accessAliases] } : {}),
    ...(nav.parent ? { parent: nav.parent } : {}),
    ...(nav.adminOnly ? { adminOnly: true } : {}),
  };
}

/** The catalogue, generated: FEATURE_SECTIONS in order, each holding its registry entries in registry order. */
export const SIDEBAR_SECTIONS: SidebarSection[] = FEATURE_SECTIONS.map((section: FeatureSection) => {
  const pages: SidebarPage[] = [];
  for (const entry of FEATURE_REGISTRY) {
    if (entry.nav.section !== section.key) continue;
    if (entry.nav.dividerBefore) pages.push({ divider: true, label: entry.nav.dividerBefore, path: "" });
    pages.push(toSidebarPage(entry));
  }
  return {
    key: section.key,
    label: section.label,
    ...(section.adminOnly ? { adminOnly: true } : {}),
    pages,
  };
});

/**
 * Every governable page path (role editor depends on this). Includes navHidden pages
 * (they still need a permission checkbox) and EXCLUDES dividers (they have no path).
 */
export const ALL_PAGE_PATHS = SIDEBAR_SECTIONS.flatMap((s) =>
  s.pages.filter((p) => !p.divider).map((p) => p.path)
);

/**
 * Every live App route represented by a catalog Page. Usually this is identical
 * to ALL_PAGE_PATHS; a multi-route Page such as BOM Suite contributes its live
 * tab routes through `accessAliases`.
 */
export const ALL_PAGE_ROUTE_PATHS = SIDEBAR_SECTIONS.flatMap((s) =>
  s.pages
    .filter((p) => !p.divider)
    .flatMap((p) => [p.path, ...(p.accessAliases ?? [])])
);

/**
 * API-route equivalences (#1836). Analyst reports are SELF_CONTENT: owner-scoped
 * rows that only exist because a chat conversation built them, and the route
 * handlers enforce ownership themselves. Access therefore follows the chat
 * grant — no role row ever carried "/api/analyst-reports", so every
 * non-wildcard AI-on role 403'd on list/print/pdf/xlsx/doc/regenerate while
 * create_report/get_report worked inside chat. Canonicalising here (rather than
 * editing every live role row) keeps custom roles covered automatically and
 * makes a chat revoke override revoke report downloads with it.
 */
export const API_PATH_EQUIVALENCES: Record<string, string> = {
  "/api/analyst-reports": "/api/chat",
};

/**
 * Equivalent route -> canonical grant. Retired `aliases` and live
 * `accessAliases` both participate in RBAC canonicalisation; only retired
 * aliases participate in browser redirects. Every alias resolves to the entry's
 * `capability` (the allowed_pages grant that gates the page), and a route whose
 * capability is not itself resolves there too, so the gate honours the
 * registry's capability field. API_PATH_EQUIVALENCES rides along so API
 * surfaces that are semantically one RBAC unit resolve together.
 */
export const PATH_ALIASES: Record<string, string> = {
  ...Object.fromEntries(
    FEATURE_REGISTRY.flatMap((e) => [
      ...(e.route === e.capability ? [] : [[e.route, e.capability] as const]),
      ...[...(e.nav.aliases ?? []), ...(e.nav.accessAliases ?? [])].map((a) => [a, e.capability] as const),
    ]),
  ),
  ...API_PATH_EQUIVALENCES,
};

/**
 * #714: retired path -> client redirect TARGET, derived from `aliases` + `aliasTabs`.
 * Where a tab key is declared the target carries `?tab=<key>` so the old URL lands on
 * its tab of the merged page; otherwise it's the bare live path. Consumed by the
 * App.tsx redirect routes — RBAC keeps using PATH_ALIASES (pure paths).
 */
export const ALIAS_REDIRECTS: Record<string, string> = Object.fromEntries(
  FEATURE_REGISTRY.filter((e) => e.nav.aliases?.length).flatMap((e) =>
    (e.nav.aliases as readonly string[]).map((a) => {
      const tab = e.nav.aliasTabs?.[a];
      return [a, tab ? `${e.route}?tab=${tab}` : e.route] as const;
    }),
  ),
);

/**
 * #779: the write-action registry — catalogue PAGES mapped to the gated write
 * action(s) a user performs from them (the same WriteAction the server route
 * enforces via requireWriteAction). Generated from each registry entry's
 * `writes`; a page with none has no key here. Source of truth for "does this
 * page accept data entry", which governs the "Data Entry" sidebar divider
 * (tests/data-entry-divider-writes.test.ts).
 */
export const PAGE_WRITE_ACTIONS: Record<string, readonly WriteAction[]> = Object.fromEntries(
  FEATURE_REGISTRY.filter((e) => e.writes.length > 0).map((e) => [e.route, e.writes] as const),
);

/** #779: true when the page has at least one registered write action. */
export function pageHasWritePath(path: string): boolean {
  return (PAGE_WRITE_ACTIONS[path]?.length ?? 0) > 0;
}

/**
 * #914: which section does a pathname belong to? Drives the Cloudflare-style sidebar's
 * auto-expand (the fold-out containing the active route opens on navigation) and the
 * collapsed rail's active-icon highlight. Longest-path match wins so a nested page
 * (/admin/material-mapping) resolves to its own entry, not a shorter prefix (/admin) —
 * both live in "tools" today, but the tie-break keeps the lookup correct if a nested
 * page ever moves section. Detail routes (/projects/123) prefix-match their index page.
 * Returns null for non-catalog routes (/login, /portal/:token) — no section highlights.
 */
export function sectionKeyForPath(pathname: string): string | null {
  let best: { key: string; len: number } | null = null;
  for (const section of SIDEBAR_SECTIONS) {
    for (const page of section.pages) {
      if (page.divider) continue;
      for (const routePath of [page.path, ...(page.accessAliases ?? [])]) {
        const matches =
          pathname === routePath ||
          (routePath !== "/" && pathname.startsWith(routePath + "/"));
        if (matches && (!best || routePath.length > best.len)) {
          best = { key: section.key, len: routePath.length };
        }
      }
    }
  }
  return best?.key ?? null;
}
